/**
 * Shared UI copy. Centralized so labels stay consistent across the widget and
 * DirectLink booking flows (product iterates on these).
 */
export const MINIMUM_SPEND_DEPOSIT_LABEL = 'Minimum Spend Deposit';

/** Single-line deposit framing for interactive-venue-map event prices. */
export const formatMinimumSpendDeposit = (price: string): string =>
  `${price} ${MINIMUM_SPEND_DEPOSIT_LABEL}`;

/** DirectLink accept-page variant framing the deposit as the reservation total. */
export const formatTotalMinimumSpendDeposit = (price: string): string =>
  `${price} Total ${MINIMUM_SPEND_DEPOSIT_LABEL}`;

/** Second line of a priced time or table card on the booking step. */
export const MINIMUM_SPEND_PER_PERSON_LABEL = 'min spend pp';

/**
 * Caption under a restaurant Minimum spend total on the booking widget.
 * Same sentence as the member app How Dorsia Works panel: the amount applies
 * to food and drink, and tax and tip are charged on top.
 * @example MINIMUM_SPEND_PAYMENT_CAPTION
 */
export const MINIMUM_SPEND_PAYMENT_CAPTION =
  '100% of your payment goes toward your food and drink minimum. Tax and tip will be on top of this.';

/**
 * Note under the saved card on checkout and confirmation: the same card is
 * charged for tax, tip, and other overages after the table minimum.
 * @example MINIMUM_SPEND_OVERAGE_PAYMENT_NOTE
 */
export const MINIMUM_SPEND_OVERAGE_PAYMENT_NOTE =
  'This payment method will be used for any overages including tax, gratuity and all applicable fees.';

/**
 * Restaurant features map fields that control the minimum-spend caption.
 */
export interface MinimumSpendExplanationFeatures {
  /** True hides the food-and-drink caption. Missing or false shows it. */
  HideMinimumSpendExplanation?: boolean;
}

/**
 * Whether the booking widget should show the food-and-drink minimum-spend caption.
 * The caption is on by default. It hides when HideMinimumSpendExplanation is
 * true (Nova 100% globally, or a per-restaurant row set to true). A missing
 * key, including older API payloads, shows it.
 * @param features - Restaurant features map, when the payload includes one
 * @returns True when the caption should render
 * @example showsMinimumSpendExplanation({ HideMinimumSpendExplanation: true }) // false
 * @example showsMinimumSpendExplanation(undefined) // true
 */
export function showsMinimumSpendExplanation(
  features?: MinimumSpendExplanationFeatures | null
): boolean {
  return features?.HideMinimumSpendExplanation !== true;
}

/**
 * Prefix and suffix around a formatted restaurant money amount.
 */
interface RestaurantCurrencyTemplate {
  /** Text before the numeric amount, including a trailing space when the sample uses one. */
  prefix: string;
  /** Text after the numeric amount, including a leading space when the sample uses one. */
  suffix: string;
}

/**
 * Derives the currency symbol or code around an existing formatted amount.
 * Falls back to `$` when the sample is missing or has no numeric part.
 * @param formattedSample - A restaurant-formatted amount such as `£175` or `AED 175`
 * @returns Prefix and suffix to wrap a newly formatted number
 * @example restaurantCurrencyTemplate('£175') // { prefix: '£', suffix: '' }
 * @example restaurantCurrencyTemplate('AED 175') // { prefix: 'AED ', suffix: '' }
 */
export function restaurantCurrencyTemplate(
  formattedSample?: string | null
): RestaurantCurrencyTemplate {
  const sample = formattedSample?.trim() ?? '';
  const match = sample.match(/^(.*?)(\d[\d,]*(?:\.\d+)?)(.*)$/);
  if (!match) {
    return { prefix: '$', suffix: '' };
  }
  return { prefix: match[1], suffix: match[3] };
}

/**
 * Reads the numeric major-unit amount out of a formatted restaurant money string.
 * Mirrors restaurantCurrencyTemplate's number match so `£1,325.02` parses the same
 * way the symbol is derived. Returns null when there is no numeric part.
 * @param formatted - A restaurant-formatted amount such as `£1,325.02` or `AED 20`
 * @returns The amount in major units, or null
 * @example parseRestaurantCurrency('£1,325.02') // 1325.02
 * @example parseRestaurantCurrency(undefined) // null
 */
export function parseRestaurantCurrency(formatted?: string | null): number | null {
  const sample = formatted?.trim() ?? '';
  const match = sample.match(/\d[\d,]*(?:\.\d+)?/);
  if (!match) {
    return null;
  }
  const numeric = Number.parseFloat(match[0].replace(/,/g, ''));
  return Number.isFinite(numeric) ? numeric : null;
}

/**
 * Formats a restaurant money amount in major units.
 * When `formattedSample` is present, keeps that sample's currency symbol or code
 * so a GBP/EUR/AED venue does not render a dollar total.
 * @param amount - Amount in major units
 * @param formattedSample - An existing formatted restaurant amount to copy the symbol from
 * @returns Formatted currency
 * @example formatRestaurantCurrency(525) // '$525.00'
 * @example formatRestaurantCurrency(525, '£175') // '£525.00'
 */
export function formatRestaurantCurrency(amount: number, formattedSample?: string | null): string {
  const { prefix, suffix } = restaurantCurrencyTemplate(formattedSample);
  const digits = amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${prefix}${digits}${suffix}`;
}

/**
 * Inputs that compute the full restaurant table minimum for a party.
 */
export interface RestaurantTableMinimumAmountInput {
  /** Per-person table price in major units. */
  pricePerPerson?: number | null;
  /** Guest count for the booking. */
  partySize?: number | null;
  /** Restaurant fee from the preview, in major units. */
  feeRestaurant: number;
  /** Formatted restaurant fee, used when it already matches the table total. */
  feeCurrency?: string | null;
}

/**
 * Full table minimum for the party. Uses price per person times guests when
 * both are present so a per-person fee on the preview cannot stand in for the total.
 * @param input - Per-person price, party size, and restaurant fee
 * @returns Table minimum in major units
 * @example restaurantTableMinimumAmount({ pricePerPerson: 175, partySize: 3, feeRestaurant: 175 }) // 525
 */
export function restaurantTableMinimumAmount(input: RestaurantTableMinimumAmountInput): number {
  const pricePerPerson = input.pricePerPerson ?? 0;
  const partySize = input.partySize ?? 0;
  if (pricePerPerson > 0 && partySize > 0) {
    return Math.round(pricePerPerson * partySize * 100) / 100;
  }
  return input.feeRestaurant;
}

/**
 * Table minimum from the selected availability option while the preview is loading.
 * @param input - Event flag, per-person price, guest count, and optional formatted per-person price
 * @returns Amount and formatted currency, or null when the party total cannot be computed
 * @example restaurantTableMinimumFromWidget({ isEvent: false, pricePerPerson: 175, partySize: 2, formattedPricePerPerson: '£175' })
 */
export function restaurantTableMinimumFromWidget(input: {
  isEvent: boolean;
  pricePerPerson?: number | null;
  partySize?: number | null;
  /** Formatted per-person price; used so a computed party total keeps the venue symbol. */
  formattedPricePerPerson?: string | null;
}): { amount: number; currency: string } | null {
  if (input.isEvent) {
    return null;
  }
  const amount = restaurantTableMinimumAmount({
    pricePerPerson: input.pricePerPerson,
    partySize: input.partySize,
    feeRestaurant: 0,
  });
  if (amount <= 0) {
    return null;
  }
  return {
    amount,
    currency: formatRestaurantCurrency(amount, input.formattedPricePerPerson),
  };
}

/**
 * Formatted full table minimum for the party.
 * @param input - Per-person price, party size, and restaurant fee
 * @returns Formatted table minimum
 * @example restaurantTableMinimumCurrency({ pricePerPerson: 175, partySize: 3, feeRestaurant: 525, feeCurrency: '$525' }) // '$525'
 */
export function restaurantTableMinimumCurrency(input: RestaurantTableMinimumAmountInput): string {
  const amount = restaurantTableMinimumAmount(input);
  if (Math.round(amount * 100) === Math.round(input.feeRestaurant * 100) && input.feeCurrency) {
    return input.feeCurrency;
  }
  return formatRestaurantCurrency(amount, input.feeCurrency);
}

/**
 * Inputs that decide whether a booking summary is a restaurant table minimum.
 */
export interface RestaurantTableMinimumInput {
  /** Event ticket totals stay labeled Total. */
  isEvent: boolean;
  /** Amount charged today, in major units. */
  totalPaid: number;
  /** Restaurant fee from the preview, in major units. */
  feeRestaurant: number;
  /** Formatted restaurant fee. Empty means the fee is not displayable. */
  feeCurrency?: string | null;
  /** Per-person table price. A positive value means the fee is a table minimum. */
  pricePerPerson?: number | null;
}

/**
 * Whether the summary should use the Minimum spend row instead of Subtotal and Total.
 * A charged table minimum still qualifies: the guest pays it now, and it applies to the meal.
 * A zero charge with a restaurant fee keeps the older Minimum spend row.
 * Event bookings, and restaurant fees with no per-person price that are actually charged, stay Total.
 * @param input - Preview amounts for the current booking
 * @returns True when the summary should render Minimum spend
 * @example isRestaurantTableMinimum({ isEvent: false, totalPaid: 350, feeRestaurant: 350, feeCurrency: '$350', pricePerPerson: 175 }) // true
 * @example isRestaurantTableMinimum({ isEvent: true, totalPaid: 350, feeRestaurant: 350, feeCurrency: '$350', pricePerPerson: 175 }) // false
 */
export function isRestaurantTableMinimum(input: RestaurantTableMinimumInput): boolean {
  if (input.isEvent || input.feeRestaurant <= 0 || !input.feeCurrency) {
    return false;
  }

  return input.totalPaid === 0 || (input.pricePerPerson ?? 0) > 0;
}

/**
 * Inputs that decide whether the Amount Due (card charge) row should render.
 */
export interface RestaurantAmountDueInput {
  /** True when credits or Fun Coupons reduce the card charge. */
  walletApplied: boolean;
  /** What the card is charged now, in major units. */
  cardChargeAmount: number;
  /** Per-person table price, for the bold minimum. */
  pricePerPerson?: number | null;
  /** Guest count, for the bold minimum. */
  partySize?: number | null;
  /** Restaurant fee from the preview, in major units. */
  feeRestaurant: number;
}

/**
 * Whether to show the Amount Due row (the card charge) on checkout and payment.
 * Show it when the wallet reduces the charge, or when the charge differs from the
 * bold Minimum spend (price per person times guests). A charge that equals the
 * minimum with no wallet needs no row: the bold Minimum spend already states it,
 * and a separate row would duplicate it. A zero charge shows nothing.
 *
 * The charge can exceed the minimum even with no wallet: Reservation::calculateTotalPaid
 * adds prepaid tax and service charges for special integrations, and product fees
 * when ProductFees is on, so hiding the row would leave the real charge unstated.
 * @param input - Wallet state, card charge, and the minimum-spend inputs
 * @returns True when the Amount Due row should render
 * @example restaurantAmountDueApplies({ walletApplied: false, cardChargeAmount: 575, pricePerPerson: 175, partySize: 3, feeRestaurant: 525 }) // true
 * @example restaurantAmountDueApplies({ walletApplied: false, cardChargeAmount: 525, pricePerPerson: 175, partySize: 3, feeRestaurant: 525 }) // false
 */
export function restaurantAmountDueApplies(input: RestaurantAmountDueInput): boolean {
  if (input.cardChargeAmount <= 0) {
    return false;
  }
  if (input.walletApplied) {
    return true;
  }
  const minimum = restaurantTableMinimumAmount({
    pricePerPerson: input.pricePerPerson,
    partySize: input.partySize,
    feeRestaurant: input.feeRestaurant,
  });
  return Math.round(input.cardChargeAmount * 100) !== Math.round(minimum * 100);
}
